import { type ReactNode, useCallback, useState } from 'react';
import { useLocation } from 'react-router';

import { type NavItem, Sidebar } from './sidebar';

const COLLAPSE_KEY = 'cyrino:sidebar-collapsed';
const storedCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === 'true';
  } catch {
    return false;
  }
};

interface AppShellProps {
  productName: string;
  sectionName: string;
  navItems: NavItem[];
  canAccess: (permission?: string) => boolean;
  fullName: string | undefined;
  subtitle: string | undefined;
  onSignOut: () => void;
  /** Rendered at the bottom of the sidebar — a build badge, and the like. */
  footer?: ReactNode;
  /** Extra items in the account dropdown, below the identity label — a role switcher, and the like. */
  accountMenuExtra?: ReactNode;
  children: ReactNode;
}

export function AppShell({
  productName,
  sectionName,
  navItems,
  canAccess,
  fullName,
  subtitle,
  onSignOut,
  footer,
  accountMenuExtra,
  children,
}: AppShellProps) {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(storedCollapsed);
  const toggle = useCallback(() => {
    setCollapsed((previous) => {
      const next = !previous;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, String(next));
      } catch {
        /* storage is optional */
      }
      return next;
    });
  }, []);

  return (
    <div className="flex h-full">
      <Sidebar
        productName={productName}
        sectionName={sectionName}
        items={navItems}
        canAccess={canAccess}
        collapsed={collapsed}
        onToggle={toggle}
        fullName={fullName}
        subtitle={subtitle}
        onSignOut={onSignOut}
        footer={footer}
        accountMenuExtra={accountMenuExtra}
      />
      <main className="flex min-w-0 flex-1 flex-col overflow-y-auto px-5 pt-7 sm:px-8">
        {/* The key sits on this wrapper, not on <main>: changing it remounts
         * the subtree, which is what restarts the CSS animation on every
         * navigation. Keying <main> itself would also recreate the scroll
         * container and lose the scroll position.
         *
         * `flex min-h-0 flex-1 flex-col` gives every page a bounded-height
         * flex column to opt into (see DataTable's `fillHeight`) without
         * changing anything for a page that doesn't: a shorter page's
         * content just sits at the top with empty space below, and a
         * taller one still overflows this wrapper and scrolls inside
         * <main>, same as before — neither has flex-grow children, so this
         * wrapper's own bounded height never clips their content.
         *
         * The bottom gap is a spacer at the end of this wrapper, not padding
         * on <main>: a page taller than the window overflows this wrapper,
         * and a scroll container's bottom padding is not added after
         * overflowing content, so the last card sat flush on the window's
         * edge. The spacer is laid out after the last child even when they
         * overflow, so it scrolls into view below it. */}
        <div
          key={pathname}
          className="flex min-h-0 flex-1 flex-col animate-page-enter after:block after:h-7 after:shrink-0 after:content-['']"
        >
          {children}
        </div>
      </main>
    </div>
  );
}
